import React from 'react';
import { Field } from 'formik';
import { FormGroup, TextboxField, SelectDropdownField, Text } from '@tradingblock/components';
import styles from '../../../pages/HomePagePolish.module.css';
import SecurityQuestionRow from './components/SecurityQuestionRow';
import usePasswordStrength from '../../../hooks/usePasswordStrength';

interface LoginSecurityStepProps {
  errors: any;
  touched: any;
  values: any;
  stepCompleted: boolean;
  getSecurityQuestionTypes: (index: number) => string[];
  TotalSecurityChallenges: number;
  userRequirements?: any;
  hasAttemptedSubmit?: boolean;
}

const LoginSecurityStep: React.FC<LoginSecurityStepProps> = React.memo(
  ({
    errors,
    touched,
    values,
    stepCompleted,
    getSecurityQuestionTypes,
    TotalSecurityChallenges,
    userRequirements,
    hasAttemptedSubmit = false,
  }) => {
    const { strength, class: strengthClass, text: strengthText } = usePasswordStrength(
      values.password,
      styles,
      userRequirements,
      values.userName,
      values.primaryAccountHolder && values.primaryAccountHolder.firstName,
      values.primaryAccountHolder && values.primaryAccountHolder.lastName
    );

    // Calculate password requirements for display
    const getPasswordRequirements = () => {
      if (!userRequirements) return null;

      const { passwordRequirements } = userRequirements;
      return {
        minLength: passwordRequirements.minLength || 8,
        maxLength: passwordRequirements.maxLength || 128,
        minLower: passwordRequirements.minLowerCount || 1,
        minUpper: passwordRequirements.minUpperCount || 1,
        minDigits: passwordRequirements.minDigitCount || 1,
        minSpecial: passwordRequirements.minSpecialCount || 1,
      };
    };

    const requirements = getPasswordRequirements();

    // Username validation helpers
    const usernameValidations = {
      minLength: values.userName && values.userName.length >= 6,
      maxLength: values.userName && values.userName.length <= 30,
      validChars: values.userName && /^[a-zA-Z0-9_-]+$/.test(values.userName),
      notEmail:
        values.userName &&
        values.primaryAccountHolder &&
        values.primaryAccountHolder.email &&
        values.userName !== values.primaryAccountHolder.email,
    };

    // Password validation helpers
    const passwordValidations = {
      minLength: values.password && values.password.length >= ((requirements && requirements.minLength) || 8),
      maxLength: values.password && values.password.length <= ((requirements && requirements.maxLength) || 128),
      hasLower: values.password && /[a-z]/.test(values.password),
      hasUpper: values.password && /[A-Z]/.test(values.password),
      hasDigit: values.password && /[0-9]/.test(values.password),
      hasSpecial: values.password && /[!@#$%^&*(),.?":{}|<>]/.test(values.password),
      notUsername:
        values.password && values.userName && !values.password.toLowerCase().includes(values.userName.toLowerCase()),
      notFirstName:
        values.password &&
        values.primaryAccountHolder &&
        values.primaryAccountHolder.firstName &&
        !values.password.toLowerCase().includes(values.primaryAccountHolder.firstName.toLowerCase()),
      notLastName:
        values.password &&
        values.primaryAccountHolder &&
        values.primaryAccountHolder.lastName &&
        !values.password.toLowerCase().includes(values.primaryAccountHolder.lastName.toLowerCase()),
    };

    return (
      <>
        <div className="card h-100 mb-2" style={{ background: 'transparent' }}>
          <div className="card-body p-0">
            <div className="login-info-section">
              <div className="field-group">
                <div className="stacked-fields">
                  <FormGroup>
                    <Field component={TextboxField} id="userName" autoComplete="username" defaultLabelId="userName" />
                    {/* Simplified Username Requirements - only show when there are issues */}
                    {(values.userName || touched.userName) &&
                      (() => {
                        const unmetRequirements = [];
                        if (!usernameValidations.minLength || !usernameValidations.maxLength) {
                          unmetRequirements.push('6-30 characters');
                        }
                        if (!usernameValidations.validChars) {
                          unmetRequirements.push('Letters, numbers, _- only');
                        }
                        if (!usernameValidations.notEmail) {
                          unmetRequirements.push('Not your email');
                        }
                        return unmetRequirements.length > 0 ? (
                          <div className={styles.compactRequirements} style={{ width: '100%', maxWidth: '100%' }}>
                            <div className={styles.requirementsHeader}>
                              <small>{unmetRequirements.join(' • ')}</small>
                            </div>
                          </div>
                        ) : null;
                      })()}
                  </FormGroup>
                  <FormGroup>
                    <Field
                      component={TextboxField}
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      allowUnmask={true}
                      defaultLabelId="password"
                    />
                    {values.password && (
                      <div style={{ marginTop: '0.75rem', marginBottom: '0', paddingLeft: 0, marginLeft: 0, paddingBottom: 0, paddingTop: 0 }}>
                        <small
                          className={`text-${strengthText.toLowerCase() === 'weak'
                              ? 'danger'
                              : strengthText.toLowerCase() === 'medium'
                                ? 'warning'
                                : 'success'
                            }`}
                          style={{ margin: 0, padding: 0, display: 'block' }}
                        >
                          {strengthText}
                        </small>
                      </div>
                    )}
                    {/* Simplified Password Requirements - only show unmet requirements */}
                    {(values.password || touched.password) &&
                      (() => {
                        const unmetRequirements = [];
                        if (!passwordValidations.minLength || !passwordValidations.maxLength) {
                          unmetRequirements.push(
                            `${requirements ? `${requirements.minLength}-${requirements.maxLength}` : '8-128'
                            } characters`
                          );
                        }
                        if (!passwordValidations.hasLower) {
                          unmetRequirements.push('lowercase');
                        }
                        if (!passwordValidations.hasUpper) {
                          unmetRequirements.push('uppercase');
                        }
                        if (!passwordValidations.hasDigit) {
                          unmetRequirements.push('number');
                        }
                        if (!passwordValidations.hasSpecial) {
                          unmetRequirements.push('special character');
                        }
                        if (
                          !passwordValidations.notUsername ||
                          !passwordValidations.notFirstName ||
                          !passwordValidations.notLastName
                        ) {
                          unmetRequirements.push('Cannot contain username or name');
                        }

                        return unmetRequirements.length > 0 ? (
                          <div className={styles.compactRequirements} style={{ width: '100%', maxWidth: '100%' }}>
                            <div className={styles.requirementsHeader}>
                              <small>
                                <Text id="password.requiredPrefix" page="secure" />
                                {unmetRequirements.join(' • ')}
                              </small>
                            </div>
                          </div>
                        ) : null;
                      })()}
                  </FormGroup>
                  <FormGroup>
                    <Field
                      component={TextboxField}
                      id="passwordConfirm"
                      type="password"
                      autoComplete="new-password"
                      allowUnmask={true}
                      defaultLabelId="passwordConfirm"
                    />
                  </FormGroup>

                  {/* Security Questions */}
                  <div className="security-questions-section">
                    {Array.from({ length: TotalSecurityChallenges }).map((_, idx) => (
                      <div key={idx} className="mb-3">
                        <SecurityQuestionRow
                          index={idx}
                          questionOptions={getSecurityQuestionTypes(idx)}
                          questionFieldId={`securityChallenges[${idx}].securityQuestionType`}
                          answerFieldId={`securityChallenges[${idx}].answer`}
                          questionLabel={
                            <Text
                              id="securityChallenges.securityQuestionType"
                              type="field"
                              data={{ number: idx + 1 }}
                            />
                          }
                          isSearchable={false}
                          menuPlacement="top"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {stepCompleted && (
          <div className={styles.stepSuccess}>
            <i className="fas fa-check-circle" />
            <span>
              <Text id="loginCredentialsCompletedSuccessfully" page="home" />
            </span>
          </div>
        )}
      </>
    );
  }
);

export default LoginSecurityStep;
